import { CheckCircle, Clock, Warning } from "@phosphor-icons/react";

/**
 * Compact "Actioned By" badge for list rows.
 * Shows: ⚪ Pending • 🔵 Pinged • 🟡 Amber • 🔴 Critical • 🟢 ✓ Name
 */
export default function SLAStatusBadge({ entity, className = "" }) {
  if (!entity) return null;
  const state = entity.sla_state || (entity.actioned_at ? "actioned" : "pending");

  if (state === "actioned") {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 ${className}`}
        title={`Actioned by ${entity.actioned_by_name || ""}`}
        data-testid="sla-badge-actioned"
      >
        <CheckCircle size={11} weight="fill" />
        <span className="truncate max-w-[120px]">{entity.actioned_by_name || "Actioned"}</span>
      </span>
    );
  }

  if (state === "critical") {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 animate-pulse ${className}`}
        title="CRITICAL — escalated, no one has actioned this yet"
        data-testid="sla-badge-critical"
      >
        <Warning size={11} weight="fill" /> CRITICAL
      </span>
    );
  }

  if (state === "amber") {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 ${className}`}
        title="AMBER — overdue, please action"
        data-testid="sla-badge-amber"
      >
        <Warning size={11} weight="fill" /> AMBER
      </span>
    );
  }

  if (state === "info_pinged") {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 ${className}`}
        title="Pinged — awaiting action"
        data-testid="sla-badge-pinged"
      >
        <Clock size={11} /> Pinged
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 ${className}`}
      title="Pending — no one has claimed this yet"
      data-testid="sla-badge-pending"
    >
      <Clock size={11} /> Pending
    </span>
  );
}

/** Returns a Tailwind class for left-border tint of a list row based on SLA state. */
export function slaRowAccent(entity) {
  const state = entity?.sla_state || (entity?.actioned_at ? "actioned" : "pending");
  switch (state) {
    case "critical": return "border-l-2 border-l-red-500";
    case "amber":    return "border-l-2 border-l-amber-400";
    case "info_pinged": return "border-l-2 border-l-blue-400";
    case "actioned": return "border-l-2 border-l-emerald-500";
    default: return "";
  }
}
